import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '' }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <motion.button
      onClick={toggleTheme}
      className={`
        group relative w-12 h-12 rounded-full transition-all duration-300 ease-in-out
        bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm
        border border-gray-200/50 dark:border-slate-700/50
        hover:bg-white dark:hover:bg-slate-800
        hover:shadow-lg hover:shadow-blue-100/50 dark:hover:shadow-slate-900/50
        hover:scale-105 active:scale-95
        focus:outline-none focus:ring-2 focus:ring-blue-400/50 dark:focus:ring-blue-500/50
        ${className}
      `}
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.05 }}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Sun Icon */}
        <motion.div
          animate={{
            rotate: theme === 'dark' ? 180 : 0,
            scale: theme === 'dark' ? 0 : 1,
            opacity: theme === 'dark' ? 0 : 1,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="absolute flex items-center justify-center"
        >
          <Sun className="w-5 h-5 text-amber-500" />
        </motion.div>

        {/* Moon Icon */}
        <motion.div
          animate={{
            rotate: theme === 'light' ? -180 : 0,
            scale: theme === 'light' ? 0 : 1,
            opacity: theme === 'light' ? 0 : 1,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="absolute flex items-center justify-center"
        >
          <Moon className="w-5 h-5 text-blue-400" />
        </motion.div>
      </div>

      {/* Tooltip */}
      <div className="
        absolute -top-12 left-1/2 transform -translate-x-1/2
        px-3 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900
        text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100
        transition-opacity duration-200 pointer-events-none
        whitespace-nowrap z-50
      ">
        {theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        <div className="
          absolute top-full left-1/2 transform -translate-x-1/2
          w-0 h-0 border-l-4 border-r-4 border-t-4
          border-transparent border-t-gray-900 dark:border-t-white
        " />
      </div>

      {/* Subtle Ring Effect on Hover */}
      <motion.div
        className="absolute inset-0 rounded-full border-2 border-blue-400/0 dark:border-blue-500/0"
        whileHover={{
          borderColor: theme === 'light' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(96, 165, 250, 0.3)',
        }}
        transition={{ duration: 0.2 }}
      />
    </motion.button>
  );
};

export default ThemeToggle;